<div class="km-page w-full max-w-[1600px] mx-auto px-3 sm:px-4 pb-10 pt-4 sm:pt-6">
  <header class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">

    <div>
      <h1 class="text-2xl sm:text-3xl font-semibold text-[#141437] dark:text-white tracking-tight">
        Key Manager
      </h1>
      <p class="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-none">
        Manage API keys and issue time-bound proxy URLs for system-to-system access. Raw secrets are never embedded in
        shareable URLs.
      </p>
    </div>
    <div class="flex flex-wrap items-center gap-2 shrink-0">
      <app-button variant="primary" [iconNameV2]="'Plus'" (buttonClick)="openCreateKeyDialog()"
        className="h-11 px-4 flex items-center gap-2">
        Create key
      </app-button>
      <app-button variant="secondary" [iconNameV2]="'RefreshCw'" (buttonClick)="loadKeys()" [disabled]="isListLoading()"
        className="shrink-0 h-11 px-4 flex items-center gap-2 border border-slate-300 dark:border-slate-600"
        aria-label="Refresh key list">
        Refresh
      </app-button>
      <app-button variant="outline" [iconNameV2]="'ShieldCheck'" (buttonClick)="openAllowAccessDialog()"
        className="shrink-0 h-11 px-4 flex items-center gap-2 border border-violet-500 dark:border-violet-500 text-violet-600 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-white/10"
        aria-label="Allow access">
        Allow Access
      </app-button>
    </div>
  </header>

  <!-- Stats -->
  <section class="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6" aria-label="Key statistics">

    @for (card of statCards(); track card.label) {
    <div class="card-bg rounded-2xl p-4 border border-slate-200/80 dark:border-slate-700/80 shadow-sm">
      <div class="flex items-center justify-between gap-2">
        <span class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {{ card.label }}
        </span>
        <app-icon-v2 [name]="card.icon" [size]="18" customClass="text-slate-400 dark:text-slate-500" />
      </div>
      <p class="mt-2 text-2xl font-semibold tabular-nums text-[#141437] dark:text-white">
        {{ card.value }}
      </p>
    </div>
    }
  </section>

  <!-- Filters: higher z-index so open dropdowns paint above the keys table (sibling stacking) -->
  <section
    class="card-bg rounded-2xl p-4 sm:p-5 mb-6 border border-slate-200/80 dark:border-slate-700/80 relative z-[100] isolate">
    <div class="flex flex-col gap-3 sm:gap-4">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
        <h2 class="text-sm font-semibold text-slate-800 dark:text-slate-100">Filters</h2>
        <app-button variant="outline" size="sm" [iconNameV2]="'X'" (buttonClick)="clearFilters()"
          [disabled]="!hasActiveFilters()"
          className="self-start sm:self-auto shrink-0 inline-flex items-center justify-center gap-2 h-9 px-3 border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 whitespace-nowrap disabled:opacity-40"
          aria-label="Clear all filters">
          Clear filters
        </app-button>
      </div>
      <div class="flex flex-col lg:flex-row lg:items-end gap-3 sm:gap-4">
        <div class="flex-[1.5] min-w-[200px]">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2" for="km-search">
            Search
          </label>
          <div class="relative">
            <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <app-icon-v2 name="Search" [size]="18" customClass="text-slate-400" />
            </span>
            <input id="km-search" type="search" autocomplete="off" [value]="searchQuery()"
              (input)="onSearchInput($any($event.target).value)" placeholder="Name, purpose, prefix, or id"
              class="km-input w-full h-11 pl-10 pr-3 rounded-xl text-sm" />
          </div>
        </div>

        <div class="flex-1 min-w-[160px] relative z-[101]">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Status</label>
          <app-dropdown [options]="statusOptions" [selectedValue]="statusFilter()" selectionMode="single"
            placeholder="Status" variant="themeBase" [fullWidth]="true" size="lg" menuClass="!z-[200] shadow-xl"
            [tooltipFixedStrategy]="true" (selectionChange)="onStatusFilter($event)" />
        </div>

        <div class="flex-1 min-w-[160px] relative z-[101]">
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Provider</label>
          <app-dropdown [options]="providerOptions()" [selectedValue]="providerFilter()" selectionMode="single"
            placeholder="Provider" variant="themeBase" [fullWidth]="true" size="lg" menuClass="!z-[200] shadow-xl"
            [tooltipFixedStrategy]="true" (selectionChange)="onProviderFilter($event)" />
        </div>
      </div>
    </div>
  </section>

  <!-- Table: stack below filter row so dropdowns are not covered -->
  <section class="card-bg rounded-2xl border border-slate-200/80 dark:border-slate-700/80 overflow-hidden relative z-0">
    @if (listError()) {

    <div class="p-8 text-center">
      <p class="text-red-600 dark:text-red-400 mb-4">{{ listError() }}</p>
      <app-button variant="primary" (buttonClick)="loadKeys()">Try again</app-button>
    </div>
    } @else if (filteredKeys().length === 0) {
    <div class="py-16 text-center text-slate-500 dark:text-slate-400 px-4">
      <app-icon-v2 name="Inbox" [size]="40" customClass="mx-auto mb-3 block opacity-60 text-slate-400" />
      @if (keys().length === 0) {
      <p class="font-medium text-slate-700 dark:text-slate-300">No API keys yet</p>
      <p class="text-sm mt-1 mb-4">Create a key to register a provider secret and get an access token.</p>
      } @else {
      <p class="font-medium text-slate-700 dark:text-slate-300">No keys match your filters</p>
      <p class="text-sm mt-1 mb-4">Adjust search, clear filters, or create a new key.</p>
      }
    </div>
    } @else {
    <div class="divide-y divide-slate-100 dark:divide-slate-800/80">
      @for (key of filteredKeys(); track key.pid) {
      <article class="km-key-card hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
        <!-- Title row: name + horizontal icon toolbar (top right) -->
        <div class="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3">
          <h3 class="text-base font-semibold text-[#141437] dark:text-white truncate min-w-0 pr-2">
            {{ key.name }}
          </h3>
          <div class="km-key-actions flex items-center justify-end flex-nowrap shrink-0" role="toolbar"
            [attr.aria-label]="'Actions for ' + key.name">
            @if (canRevokeOrRotate(key.status)) {
            <div
              class="flex items-center justify-center p-2 flex-nowrap shrink-0 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 rounded-lg cursor-pointer"
              (click)="openDetails(key)">
              <app-icon-v2 name="PanelRight" [size]="18" customClass="text-slate-600 dark:text-slate-300" />
            </div>
            <div
              class="flex items-center justify-center p-2 flex-nowrap shrink-0 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 rounded-lg cursor-pointer"
              (click)="requestRevokeKey(key)">
              <app-icon-v2 name="Trash" [size]="18" customClass="text-red-500 dark:text-red-400" />
            </div>
            }
          </div>
        </div>
        <!-- Metadata grid -->
        <div
          class="px-4 sm:px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8 gap-x-4 gap-y-3 text-sm">
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Provider
            </p>
            <p class="text-slate-700 dark:text-slate-200">{{ formatProvider(key.provider) }}</p>
          </div>

          <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Status
            </p>
            <span [class]="statusBadgeClass(key.status)">{{ key.status }}</span>
          </div>
          <div class="min-w-0">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Prefix
            </p>
            <p class="font-mono text-xs text-slate-600 dark:text-slate-400 truncate">
              {{ key.displayPrefix }}••••
            </p>
          </div>
          <div class="min-w-0">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Purpose
            </p>
            <p class="text-slate-600 dark:text-slate-300 truncate" [title]="key.purpose">{{ key.purpose }}</p>
          </div>
          <!-- <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Access uses
            </p>
            <p class="text-slate-700 dark:text-slate-200 tabular-nums">{{ key.accessCount }}</p>
          </div> -->
          <div class="min-w-0">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Created by
            </p>
            <p class="text-slate-600 dark:text-slate-300 truncate" [title]="key.createdByUserName ?? ''">
              {{ key.createdByUserName ?? '—' }}
            </p>
          </div>
          <div>
            <p class="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
              Created
            </p>
            <p class="text-slate-500 dark:text-slate-400 whitespace-nowrap">
              {{ key.createdAt | date: 'mediumDate' }}
            </p>
          </div>
        </div>
      </article>
      }
    </div>
    }

    @if (keys().length > 0) {
    <div class="mt-6 border-t border-slate-200 dark:border-slate-800 pt-4">
      <!-- Search and the provider filter narrow the loaded page client-side (the list API takes neither),
           so the footer counts what is shown, not the server total. -->
      <app-pagination [currentPage]="currentPage()" [pageSize]="pageSize()"
        [totalItems]="searchQuery().trim() || providerFilter() !== 'ALL' ? filteredKeys().length : totalItems()"
        (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
      </app-pagination>
    </div>
    }
  </section>

  <!-- Audit Logs -->
  <div class="mt-8 pb-10">
    <app-audit-log-panel [entityType]="auditEntityTypes" title="Key Manager Audit Trails"
      [productPid]="sidebarProductPid() || undefined" [showProductFilter]="false">
    </app-audit-log-panel>
  </div>
</div>

<!-- Details panel -->
<app-slide-panel [open]="detailsOpen()" [title]="selectedKey()?.name ?? 'Key details'" panelClass="max-w-lg"
  [outsideClick]="true" (closed)="closeDetails()">
  @if (selectedKey(); as k) {
  <div class="space-y-4 text-sm">
    <dl class="space-y-3">
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Provider</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ formatProvider(k.provider) }}</dd>
      </div>

      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</dt>
        <dd class="mt-0.5"><span [class]="statusBadgeClass(k.status)">{{ k.status }}</span></dd>
      </div>
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Purpose</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ k.purpose }}</dd>
      </div>
      <!-- <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Access uses</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100 tabular-nums">{{ k.accessCount }}</dd>
      </div> -->
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Created by</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ k.createdByUserName ?? '—' }}</dd>
      </div>
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Display prefix</dt>
        <dd class="mt-0.5 font-mono text-xs">{{ k.displayPrefix }}••••</dd>
      </div>
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Created</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ k.createdAt | date: 'medium' }}</dd>
      </div>
      <!-- <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Updated</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ k.updatedAt | date: 'medium' }}</dd>
      </div> -->
      @if (k.lastRotatedAt) {
      <div>
        <dt class="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">Last rotated</dt>
        <dd class="mt-0.5 text-slate-800 dark:text-slate-100">{{ k.lastRotatedAt | date: 'medium' }}</dd>
      </div>
      }
    </dl>
    <div class="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-4">
      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          Copy to clipboard
        </p>
        <div class="flex flex-wrap gap-2">
          <app-button variant="outline" size="sm" [iconNameV2]="'Lock'" (buttonClick)="copyDecryptedKey(k)"
            className="flex items-center gap-2" [disabled]="isKeyActionLocked(k)">
            {{ panelActionIs(k.pid, 'copyKey') ? 'Copying…' : 'Copy API key' }}
          </app-button>
          <app-button variant="outline" size="sm" [iconNameV2]="'Copy'" (buttonClick)="copyAccessUrl(k)"
            className="flex items-center gap-2" [disabled]="isKeyActionLocked(k)">
            {{ panelActionIs(k.pid, 'copyUrl') ? 'Copying…' : 'Copy access URL' }}
          </app-button>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
          Copy API key is the provider secret. Copy access URL is the full proxy link from the server (includes
          <code class="text-[11px]">pid</code> and token query).
        </p>
      </div>

      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">
          Proxy access
        </p>
        <div class="flex flex-wrap gap-2">
          <app-button variant="secondary" size="sm" [iconNameV2]="'RefreshCw'" className="flex items-center gap-2"
            (buttonClick)="requestRegenerateAccessToken(k)" [disabled]="isKeyActionLocked(k)">
            {{ panelActionIs(k.pid, 'regenerate') ? 'Regenerating…' : 'Regenerate access token' }}
          </app-button>
          <app-button variant="secondary" size="sm" [iconNameV2]="'Globe'" (buttonClick)="generateAccessUrl(k)"
            className="flex items-center gap-2" [disabled]="isKeyActionLocked(k)">
            {{
            accessBusy() && accessUrlForRow()?.pid === k.pid ? 'Loading…' : 'Show access URL'
            }}
          </app-button>
        </div>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-2">
          Regenerating creates a new token—update shared links afterward. Show access URL opens a dialog to review,
          mask,
          or copy the URL.
        </p>
      </div>
    </div>

    @if (canRevokeOrRotate(k.status)) {
    <div class="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-700">
      <div>
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
          Rotate key
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Enter the new API key material. The current secret will stop working after a successful rotation.
        </p>
        <div class="relative mb-2">
          <input [type]="rotateApiKeyVisible() ? 'text' : 'password'"
            class="km-input w-full h-10 pl-3 pr-10 rounded-xl text-sm" [value]="rotateNewApiKey()"
            (input)="rotateNewApiKey.set($any($event.target).value)" (blur)="rotateApiKeyTouched.set(true)"
            autocomplete="new-password" autocapitalize="off" spellcheck="false" placeholder="New API key" />
          <button type="button"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition-colors"
            (click)="rotateApiKeyVisible.set(!rotateApiKeyVisible())"
            [attr.aria-label]="rotateApiKeyVisible() ? 'Hide API key' : 'Show API key'">
            <app-icon-v2 [name]="rotateApiKeyVisible() ? 'Eye' : 'EyeOff'" [size]="18" />
          </button>
        </div>
        @if (rotateApiKeyTouched()) {
        @if (!rotateNewApiKey()) {
        <p class="text-[11px] text-red-500 mb-2 ml-1">New API key is required</p>
        } @else if (rotateApiKeyHasSpaces()) {
        <p class="text-[11px] text-red-500 mb-2 ml-1">Spaces are not allowed in API key</p>
        } @else if (rotateNewApiKey().length < 8) { <p class="text-[11px] text-red-500 mb-2 ml-1">API key must be at
          least 8 characters</p>
          }
          }
          <app-button variant="primary" size="sm" [iconNameV2]="'RotateCcw'" (buttonClick)="submitRotateKey(k)"
            className="flex items-center gap-2" [disabled]="isKeyActionLocked(k)">
            {{ panelActionIs(k.pid, 'rotate') ? 'Rotating…' : 'Rotate with new key' }}
          </app-button>
      </div>
    </div>
    }
  </div>
  }
</app-slide-panel>

<!-- Create key dialog -->
@if (createDialogOpen()) {
<div class="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true"
  aria-labelledby="km-create-title">
  <div class="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-[2px]" (click)="closeCreateKeyDialog()"
    tabindex="-1"></div>
  <div
    class="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-surface-1 shadow-2xl p-5 sm:p-6">
    <div class="flex items-start justify-between gap-3 mb-4">
      <h2 id="km-create-title" class="text-lg font-semibold text-[#141437] dark:text-white">Create API key</h2>
      <button type="button" class="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
        (click)="closeCreateKeyDialog()" aria-label="Close">
        <app-icon-v2 name="X" [size]="20" customClass="text-slate-500" />
      </button>
    </div>
    <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">
      Register a provider secret for proxy access. After saving, open the key and use <strong>Generate access
        URL</strong>
      to copy the API URL (includes <code class="text-[11px]">pid</code> and <code
        class="text-[11px]">accessToken</code>).
    </p>
    <div class="space-y-3">
      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5"
          for="km-create-name">Name</label>
        <input id="km-create-name" type="text" class="km-input w-full h-10 px-3 rounded-xl text-sm"
          [value]="createName()" (input)="createName.set($any($event.target).value)"
          (blur)="createNameTouched.set(true)" autocomplete="off" placeholder="e.g. Production OpenAI" required 
          maxlength="255" />
        @if (createNameTouched()) {
          @if (!createName().trim()) {
            <p class="text-[11px] text-red-500 mt-1 ml-1">Name is required</p>
          } @else if (createName().length >= 255) {
            <p class="text-[11px] text-red-500 mt-1 ml-1">Maximum length of 255 characters reached</p>
          }

        }

      </div>
      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">Provider</label>
        <app-dropdown [options]="createProviderOptions" [selectedValue]="createProvider()" selectionMode="single"
          placeholder="Provider" variant="themeBase" [fullWidth]="true" size="lg" menuClass="!z-[220] shadow-xl"
          [tooltipFixedStrategy]="true" (selectionChange)="onCreateProviderSelect($event)" />
      </div>

      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5"
          for="km-create-purpose">Purpose</label>
        <input id="km-create-purpose" type="text" class="km-input w-full h-10 px-3 rounded-xl text-sm"
          [value]="createPurpose()" (input)="createPurpose.set($any($event.target).value)"
          (blur)="createPurposeTouched.set(true)" autocomplete="off" placeholder="What this key is used for" required />
        @if (createPurposeTouched() && !createPurpose().trim()) {
        <p class="text-[11px] text-red-500 mt-1 ml-1">Purpose is required</p>
        }
      </div>
      <div>
        <label class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5" for="km-create-secret">API
          key</label>
        <div class="relative">
          <input id="km-create-secret" [type]="createApiKeyVisible() ? 'text' : 'password'"
            class="km-input w-full h-10 pl-3 pr-10 rounded-xl text-sm" [value]="createApiKey()"
            (input)="createApiKey.set($any($event.target).value)" (blur)="createApiKeyTouched.set(true)"
            autocomplete="new-password" autocapitalize="off" spellcheck="false" placeholder="Provider API key or secret"
            required minlength="8" />
          <button type="button"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition-colors"
            (click)="createApiKeyVisible.set(!createApiKeyVisible())"
            [attr.aria-label]="createApiKeyVisible() ? 'Hide API key' : 'Show API key'">
            <app-icon-v2 [name]="createApiKeyVisible() ? 'Eye' : 'EyeOff'" [size]="18" />
          </button>
        </div>
        @if (createApiKeyTouched()) {
        @if (!createApiKey()) {
        <p class="text-[11px] text-red-500 mt-1 ml-1">API key is required</p>
        } @else if (createApiKeyHasSpaces()) {
        <p class="text-[11px] text-red-500 mt-1 ml-1">Spaces are not allowed in API key</p>
        } @else if (createApiKey().length < 8) { <p class="text-[11px] text-red-500 mt-1 ml-1">API key must be at least
          8 characters</p>
          }
          }
      </div>
    </div>
    <div class="mt-6 flex flex-wrap justify-end gap-2">
      <app-button variant="outline" (buttonClick)="closeCreateKeyDialog()" [disabled]="createBusy()">Cancel</app-button>
      <app-button variant="primary" [iconNameV2]="'Plus'" className="flex items-center gap-2"
        (buttonClick)="submitCreateKey()" [disabled]="createBusy()">
        {{ createBusy() ? 'Creating…' : 'Create key' }}
      </app-button>
    </div>
  </div>
</div>
}

<!-- Secure URL dialog -->
@if (accessDialogOpen()) {
<div class="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true"
  aria-labelledby="km-access-title">
  <div class="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-[2px]" (click)="closeAccessDialog()"
    (keydown.escape)="closeAccessDialog()" tabindex="-1"></div>
  <div
    class="relative z-10 w-full max-w-lg rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-surface-1 shadow-2xl p-5 sm:p-6">
    <div class="flex items-start justify-between gap-3 mb-4">
      <div>
        <h2 id="km-access-title" class="text-lg font-semibold text-[#141437] dark:text-white">
          Secure access URL
        </h2>
        @if (accessUrlForRow(); as row) {
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{{ row.name }}</p>
        }
      </div>
      <button type="button" class="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
        (click)="closeAccessDialog()" aria-label="Close dialog">
        <app-icon-v2 name="X" [size]="20" customClass="text-slate-500" />
      </button>
    </div>

    @if (accessUrlResult(); as r) {
    <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
      Share this URL with trusted systems only. The <code class="text-[11px]">accessToken</code> query parameter is for
      this proxy URL, not your provider API key.
    </p>
    <div
      class="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900/50 p-3 font-mono text-xs break-all text-slate-800 dark:text-slate-100">
      {{ displayUrl(r.accessUrl) }}
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
      @if (r.ttlSeconds != null) {
      <span class="inline-flex items-center gap-1.5">
        <app-icon-v2 name="Clock" [size]="16" customClass="text-slate-500" />
        Valid for about {{ r.ttlSeconds }}s
      </span>
      }
      @if (remainingLabel()) {
      <span class="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
        <app-icon-v2 name="Hourglass" [size]="16" customClass="text-amber-700 dark:text-amber-300" />
        {{ remainingLabel() }}
      </span>
      }
      @if (r.singleUse === true) {
      <span
        class="text-xs px-2 py-0.5 rounded-md bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200">
        Single-use
      </span>
      }
    </div>

    @if (accessUrlGeneratedAt(); as genAt) {
    <p class="mt-3 text-xs text-slate-500 dark:text-slate-500">
      Generated {{ genAt | date: 'medium' }}
    </p>
    }

    <div class="mt-5 flex flex-wrap gap-2">
      <app-button variant="primary" [iconNameV2]="'Copy'" className="flex items-center gap-2"
        (buttonClick)="copyUrlFromDialog()">
        Copy URL
      </app-button>
      <app-button variant="outline" [iconNameV2]="'Eye'" className="flex items-center gap-2"
        (buttonClick)="toggleRevealUrl()">
        {{ revealFullUrl() ? 'Mask URL' : 'Reveal full URL' }}
      </app-button>
      <app-button variant="ghost" (buttonClick)="closeAccessDialog()">Done</app-button>
    </div>
    }
  </div>
</div>
}

<!-- Allow Access dialog -->
@if (allowAccessDialogOpen()) {
<div class="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true"
  aria-labelledby="km-allow-access-title">
  <div class="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-[2px]" (click)="closeAllowAccessDialog()"
    tabindex="-1"></div>
  <div
    class="relative z-10 w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-surface-1 shadow-2xl p-5 sm:p-6">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3 mb-4">
      <h2 id="km-allow-access-title" class="text-lg font-semibold text-[#141437] dark:text-white">Allow Access</h2>
      <button type="button" class="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
        (click)="closeAllowAccessDialog()" aria-label="Close">
        <app-icon-v2 name="X" [size]="20" customClass="text-slate-500" />
      </button>
    </div>
    <div class="space-y-4">
      <!-- Select Items -->
      <div>
        <p class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Select Items</p>
        <div class="relative mb-2">
          <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <app-icon-v2 name="Search" [size]="16" customClass="text-slate-400" />
          </span>
          <input type="search" autocomplete="off" placeholder="Search items..."
            class="km-input w-full h-10 pl-9 pr-3 rounded-xl text-sm" [value]="allowAccessSearch()"
            (input)="allowAccessSearch.set($any($event.target).value)" />
        </div>
        <div
          class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden max-h-[220px] overflow-y-auto">
          @for (item of allowAccessFilteredItems(); track item.userId) {
          <label
            class="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 border-b border-slate-100 dark:border-slate-700/60 last:border-b-0">
            <input type="checkbox" class="w-4 h-4 rounded cursor-pointer accent-violet-600"
              [checked]="allowAccessSelected().has(item.userId)" (change)="toggleAllowAccessItem(item.userId)" />
            <span class="text-sm text-slate-800 dark:text-slate-100 select-none">{{ item.label }}</span>
          </label>
          }
          @if (allowAccessFilteredItems().length === 0) {
          <div class="px-4 py-6 text-center text-sm text-slate-400">No items match your search.</div>
          }
        </div>
      </div>
      <!-- Note -->
      <div>
        <p class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Note</p>
        <textarea rows="3" class="km-input w-full px-3 py-2.5 rounded-xl text-sm resize-none"
          placeholder="Enter notes..." [value]="allowAccessNote()"
          (input)="allowAccessNote.set($any($event.target).value)"></textarea>
      </div>
    </div>
    <!-- Footer -->
    <div class="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
      <app-button variant="outline" (buttonClick)="closeAllowAccessDialog()"
        [disabled]="createBusy()">Cancel</app-button>
      <app-button variant="primary" (buttonClick)="submitAllowAccess()" [disabled]="createBusy()">
        {{ createBusy() ? 'Processing…' : 'Allow' }}
      </app-button>
    </div>
  </div>
</div>
}